// <line-chart> — investment amount by month: one smooth accent line over a
// translucent fill. Light-DOM enhancement of server-rendered markup
// (Components::Charts::MonthlyLine renders the JSON payload and a sized
// canvas wrapper); this element only draws.
//
//   <line-chart>
//     <script type="application/json" data-chart-data>{...}</script>
//     <div class="relative h-56"><canvas></canvas></div>
//   </line-chart>

import {
	Chart,
	Filler,
	LineController,
	LineElement,
	PointElement,
} from "chart.js";
import {
	chartColor,
	ensureChartTheme,
	findCanvas,
	money,
	readPayload,
	withAlpha,
} from "./chart-theme";

type Payload = {
	currency: string;
	color: string;
	// title is the tooltip heading ("July 2026"); the axis shows the shorter
	// label ("Jul '26").
	points: { label: string; title?: string; value: number }[];
};

// The line controller/elements and the Filler plugin register lazily on the
// first line chart, so pages without one never pay for them.
let lineRegistered = false;
function ensureLineRegistered() {
	if (lineRegistered) return;
	lineRegistered = true;
	Chart.register(LineController, LineElement, PointElement, Filler);
}

class LineChart extends HTMLElement {
	#chart: Chart<"line"> | null = null;

	connectedCallback() {
		ensureChartTheme();
		ensureLineRegistered();
		const payload = readPayload<Payload>(this, "line-chart");
		const color = chartColor(payload.color);

		this.#chart?.destroy();
		this.#chart = new Chart(findCanvas(this, "line-chart"), {
			type: "line",
			data: {
				labels: payload.points.map((point) => point.label),
				datasets: [
					{
						data: payload.points.map((point) => point.value),
						borderColor: color,
						backgroundColor: withAlpha(color, 0.18),
						fill: "origin",
						borderWidth: 1.5,
						tension: 0.35,
						pointRadius: 0,
						pointHoverRadius: 3,
						pointHitRadius: 10,
						pointBackgroundColor: color,
						pointBorderColor: color,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: { duration: 300 },
				interaction: { mode: "index", intersect: false },
				plugins: {
					tooltip: {
						callbacks: {
							title: (items) => {
								const first = items[0];
								if (!first) return "";
								return payload.points[first.dataIndex]?.title ?? first.label;
							},
							label: (item) =>
								` ${money(item.parsed.y ?? 0, payload.currency)}`,
						},
					},
				},
				scales: {
					x: {
						grid: { display: false },
						border: { display: false },
						ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
					},
					y: {
						// Amounts only; no negative months exist by construction.
						min: 0,
						border: { display: false },
						grid: { color: "rgba(255, 255, 255, 0.06)" },
						ticks: {
							maxTicksLimit: 5,
							callback: (value) =>
								money(Number(value), payload.currency, "compact"),
						},
					},
				},
			},
		});
	}

	disconnectedCallback() {
		this.#chart?.destroy();
		this.#chart = null;
	}
}

if (!customElements.get("line-chart")) {
	customElements.define("line-chart", LineChart);
}
